import { Feature } from "../phonemes/features";
import { ipaToFeatures, IpaType } from "../phonemes/ipa";
import { PhonemeItem, segmentPhonemes } from "../phonemes/segmentPhonemes";

/** apply workaround for espeak bug */
export function workaround(ipa: IpaType) {
  return workaroundSyllabicBug(ipa);
}

/**
 * espeak bug: if there have only been voiceless consonants,
 *   followed by certain syllabic consonants like x̩ or χ̩
 *   followed by particular consonants like n|m|r|ɽ|ɭ,
 *   then audio playback stops working
 * workaround: insert [ə̆] before the syllabic consonant
 */
function workaroundSyllabicBug(ipa: IpaType): IpaType {
  const problem = findProblem(ipa);
  if (problem === false) {
    return ipa;
  }
  return `${problem[0]}ə̆${problem[1]}`;
}

/** if we find a problematic sequence, return the before & after portions of the string */
function findProblem(ipa: IpaType): false | [IpaType, IpaType] {
  const phonemes = segmentPhonemes(ipa, 'ipaPlus').phonemes;
  const len = phonemes.length;
  for (let i = 0; i < len; i++) {
    const p = phonemes[i].phoneme;
    const features = ipaToFeatures(p);
    if (!features) { continue; }

    if (features.includes('vowel') || features.includes('voiced')) {
      // once we hit anything with voicing, we're fine
      return false;
    }

    if (maybeProblem(p, features)) {
      // we found a potential problematic phoneme before we found a voiced phoneme,
      // some following consonants cause the bug, but it's unclear what they have in common,
      // so be safe & trigger the workaround for all consonants
      const next = phonemes[i + 1];
      if (isConsonant(next) || (isIgnorable(next) && isConsonant(phonemes[i + 2]))) {
        const pre = phonemes.slice(0, i).map(p => p.phoneme).join('');
        const post = phonemes.slice(i).map(p => p.phoneme).join('');
        return [pre, post];
      }
    }
  }
  return false;
}

function maybeProblem(p: IpaType, features: Feature[]) {
  // seems to only be syllabic & C & voiceless & (palatal | velar | uvular | glottal)
  if (!features.includes('syllabic') || !features.includes('consonant') || !features.includes('voiceless')) {
    return false;
  }
  return features.includes('palatal') || features.includes('velar') || features.includes('uvular') || features.includes('glottal');
}

function isConsonant(pi?: PhonemeItem) {
  if (!pi) { return false; }
  const features = ipaToFeatures(pi.phoneme);
  return features?.includes('consonant');
}

function isIgnorable(pi?: PhonemeItem) {
  return pi && (pi.meta === 'stress' || pi.meta === 'break');
}
